import type {
  ApiEnvelope,
  ApiMeta,
  HourlyWeatherResponse as RawHourlyWeatherResponse,
  ImpactResponse as RawImpactResponse,
  TyphoonTrack as RawTyphoonTrack,
} from "@shared/api";
import type {
  CityHourlyForecast as RawCityHourlyForecast,
  CityLocation as RawCityLocation,
  StormIdentity as RawStormIdentity,
  WindRadii as RawWindRadii,
} from "@shared/domain";

import type {
  CityHourlyForecast,
  CityLocation,
  ComparisonTrigger,
  ImpactResponse,
  StormIdentity,
  StormPoint,
  StormTrack,
  WeatherImpactRow,
  WindRadii,
} from "../domain/types";

export class ApiError extends Error {
  status: number;
  details: unknown;

  constructor(status: number, message: string, details: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

export function resolveApiUrl(pathname: string, baseUrl = import.meta.env.BASE_URL): string {
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return `${normalizedBase}api${normalizedPath}`;
}

async function requestEnvelope<T>(url: string, signal?: AbortSignal): Promise<ApiEnvelope<T>> {
  let response: Response;
  try {
    response = await fetch(url, { signal, headers: { accept: "application/json" } });
  } catch (error) {
    throw new ApiError(0, "无法连接数据服务", error);
  }
  const contentType = response.headers.get("content-type") ?? "";
  const body: unknown = contentType.includes("application/json")
    ? await response.json()
    : await response.text();
  if (!response.ok) {
    const message = typeof body === "object" && body && "error" in body
      && typeof body.error === "object" && body.error && "message" in body.error
      ? String(body.error.message)
      : typeof body === "string" && body
        ? body
        : `请求失败（${response.status}）`;
    throw new ApiError(response.status, message, body);
  }
  if (typeof body !== "object" || !body || !("data" in body) || !("meta" in body)) {
    throw new ApiError(response.status, "数据服务返回了无法识别的响应", body);
  }
  return body as ApiEnvelope<T>;
}

function normalizeIdentity(storm: RawStormIdentity): StormIdentity {
  const shortYear = String(storm.year).slice(-2);
  const sequence = storm.number === null ? "--" : String(storm.number).padStart(2, "0");
  return {
    ...storm,
    number: `${shortYear}${sequence}`,
    nameZh: storm.name,
  };
}

function normalizeWindRadii(raw: RawWindRadii): WindRadii[] {
  return raw.rings.map((ring) => ({
    windScale: ring.thresholdKt === 30 ? 7 : ring.thresholdKt === 50 ? 10 : 12,
    thresholdKt: ring.thresholdKt,
    quadrantsKm: ring.quadrantsKm,
    evidence: raw.evidence,
  }));
}

function normalizePoint(
  point: RawTyphoonTrack["observed"][number],
  windRadii: RawWindRadii[],
): StormPoint {
  const radii = windRadii.find((candidate) => Date.parse(candidate.validTime) === Date.parse(point.time));
  return {
    id: `${point.evidence}-${point.time}`,
    time: point.time,
    latitude: point.latitude,
    longitude: point.longitude,
    windSpeedKph: point.maxWindMs === null ? null : Number((point.maxWindMs * 3.6).toFixed(1)),
    pressureHpa: point.pressureHpa,
    intensity: point.intensity,
    evidence: point.evidence === "fallback" ? "insufficient" : point.evidence,
    windRadii: radii ? normalizeWindRadii(radii) : [],
  };
}

function sourceLabel(meta: ApiMeta) {
  return {
    qweather: "和风天气",
    hko: "香港天文台（降级）",
    demo: "内置演示数据",
    mixed: "中国天气 / 多源数据",
  }[meta.source];
}

function normalizeTrack(raw: RawTyphoonTrack, meta: ApiMeta): StormTrack {
  return {
    identity: normalizeIdentity(raw.storm),
    observed: raw.observed.map((point) => normalizePoint(point, raw.windRadii)),
    forecast: raw.forecast.map((point) => normalizePoint(point, raw.windRadii)),
    updatedAt: meta.updatedAt,
    sourceLabel: sourceLabel(meta),
    partial: meta.degraded,
    freshness: meta.freshness,
    warnings: meta.warnings,
    windRadiiTimeline: raw.windRadii,
  };
}

function normalizeCity(city: RawCityLocation): CityLocation {
  return {
    id: city.id,
    name: city.name,
    adm1: city.adm1,
    adm2: city.adm2,
    latitude: city.latitude,
    longitude: city.longitude,
  };
}

function normalizeForecast(forecast: RawCityHourlyForecast): CityHourlyForecast {
  return {
    id: `${forecast.locationId}-${forecast.time}`,
    locationId: forecast.locationId,
    time: forecast.time,
    weatherText: forecast.weatherText,
    rainMm: forecast.precipitationMm,
    windDirection: forecast.windDirection,
    windSpeedKph: Number((forecast.windSpeedMs * 3.6).toFixed(1)),
    windScale: forecast.beaufort,
    cloudPercent: forecast.cloudPercent,
    issuedAt: forecast.publishedAt,
  };
}

const normalizeTrigger = (trigger: RawImpactResponse["rows"][number]["comparison"]["trigger"]): ComparisonTrigger =>
  trigger === "wind" ? "wind" : trigger === "data-quality" ? "freshness" : "missing";

function normalizeImpactRow(row: RawImpactResponse["rows"][number], bucket: "1h" | "3h"): WeatherImpactRow {
  const endTime = new Date(Date.parse(row.time) + (bucket === "3h" ? 3 : 1) * 3_600_000).toISOString();
  return {
    id: row.time,
    startTime: row.time,
    endTime,
    weatherText: row.forecast?.weatherText ?? "暂无城市预报",
    windDirection: row.forecast?.windDirection ?? "暂无风向",
    windScale: row.forecast?.beaufort ?? 0,
    windSpeedKph: row.forecast ? Number((row.forecast.windSpeedMs * 3.6).toFixed(1)) : 0,
    rainMm: row.forecast?.precipitationMm ?? 0,
    impactWindScale: row.impact?.windBeaufort ?? null,
    impactRainMm: row.impact?.rainfallMm ?? null,
    confidence: row.impact?.confidence ?? "low",
    comparison: {
      status: row.comparison.status,
      trigger: normalizeTrigger(row.comparison.trigger),
      reason: row.comparison.reason,
    },
  };
}

function normalizeImpact(raw: RawImpactResponse, meta: ApiMeta): ImpactResponse {
  return {
    stormId: raw.storm.providerId,
    location: normalizeCity(raw.location),
    rows: raw.rows.map((row) => normalizeImpactRow(row, raw.bucket)),
    updatedAt: meta.updatedAt,
    methodVersion: `${raw.methods.wind} · ${raw.methods.rain} · ${raw.methods.comparison}`,
    freshness: meta.freshness,
    warnings: meta.warnings,
    disclaimer: raw.disclaimer,
  };
}

export const getActiveTyphoons = async (signal?: AbortSignal) => {
  const envelope = await requestEnvelope<RawStormIdentity[]>(resolveApiUrl("/typhoons/active"), signal);
  return envelope.data.map(normalizeIdentity);
};

export const getTyphoon = async (source: string, id: string, signal?: AbortSignal) => {
  const envelope = await requestEnvelope<RawTyphoonTrack>(
    resolveApiUrl(`/typhoons/${encodeURIComponent(source)}/${encodeURIComponent(id)}`),
    signal,
  );
  return normalizeTrack(envelope.data, envelope.meta);
};

export const searchCities = async (query: string, signal?: AbortSignal) => {
  const envelope = await requestEnvelope<RawCityLocation[]>(
    resolveApiUrl(`/locations/search?q=${encodeURIComponent(query.trim())}`),
    signal,
  );
  return envelope.data.map(normalizeCity);
};

export const getHourlyWeather = async (locationId: string, hours = 72, signal?: AbortSignal) => {
  const envelope = await requestEnvelope<RawHourlyWeatherResponse>(
    resolveApiUrl(`/weather/hourly?locationId=${encodeURIComponent(locationId)}&hours=${Math.max(1, Math.min(72, hours))}`),
    signal,
  );
  return envelope.data.hours.map(normalizeForecast);
};

export const getImpact = (
  stormId: string,
  locationId: string,
  hours = 72,
  bucket: "1h" | "3h" = "3h",
  signal?: AbortSignal,
) => requestEnvelope<RawImpactResponse>(
  resolveApiUrl(`/impact?stormId=${encodeURIComponent(stormId)}&locationId=${encodeURIComponent(locationId)}&hours=${Math.max(1, Math.min(72, hours))}&bucket=${bucket}`),
  signal,
).then((envelope) => normalizeImpact(envelope.data, envelope.meta));
